import React from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

export interface InventoryFilters {
  itemName?: string;
  unit?: string;
  stockStatus?: string;
}

interface InventoryFilterPanelProps {
  filters: InventoryFilters;
  onFilterChange: (filters: InventoryFilters) => void;
  onReset: () => void;
}

export const InventoryFilterPanel: React.FC<InventoryFilterPanelProps> = ({
  filters,
  onFilterChange,
  onReset,
}) => {
  const { t } = useTranslation();

  const handleChange = (key: keyof InventoryFilters, value: string) => {
    onFilterChange({
      ...filters,
      [key]: value || undefined,
    });
  };

  const formatUnit = (unit: string) => {
    return t(`items.units.${unit}`, { defaultValue: unit });
  };

  const hasActiveFilters = filters.itemName || filters.unit || filters.stockStatus;

  return (
    <div className="card">
      {/* Single Row Layout for Desktop */}
      <div className="flex flex-col md:flex-row md:items-end gap-4">
        {/* Item Name Search */}
        <div className="flex-1 min-w-0">
          <label className="block text-sm font-medium mb-1">
            {t('warehouse.inventory.searchItem')}
          </label>
          <input
            type="text"
            value={filters.itemName || ''}
            onChange={(e) => handleChange('itemName', e.target.value)}
            placeholder={t('warehouse.inventory.searchPlaceholder')}
            className="input-field w-full"
          />
        </div>

        {/* Unit Filter - Combobox */}
        <div className="w-full md:w-40">
          <label className="block text-sm font-medium mb-1">
            {t('warehouse.inventory.unit')}
          </label>
          <select
            value={filters.unit || ''}
            onChange={(e) => handleChange('unit', e.target.value)}
            className="input-field w-full"
          >
            <option value="">{t('common.all')}</option>
            {['M3', 'TON', 'KG', 'PIECE'].map((unit) => (
              <option key={unit} value={unit}>
                {formatUnit(unit)}
              </option>
            ))}
          </select>
        </div>

        {/* Stock Status Filter - Combobox */}
        <div className="w-full md:w-48">
          <label className="block text-sm font-medium mb-1">
            {t('warehouse.inventory.stockStatus')}
          </label>
          <select
            value={filters.stockStatus || ''}
            onChange={(e) => handleChange('stockStatus', e.target.value)}
            className="input-field w-full"
          >
            <option value="">{t('common.all')}</option>
            <option value="in_stock">{t('warehouse.inventory.inStock')}</option>
            <option value="out_of_stock">{t('warehouse.inventory.outOfStock')}</option>
            <option value="negative">{t('warehouse.inventory.negative')}</option>
          </select>
        </div>

        {/* Reset Button */}
        {hasActiveFilters && (
          <div className="w-full md:w-auto">
            <button
              onClick={onReset}
              className="btn-secondary flex items-center justify-center w-full md:w-auto whitespace-nowrap"
              title={t('warehouse.inventory.clearFilter')}
            >
              <X className="w-4 h-4 md:mr-2" />
              <span className="md:inline">{t('warehouse.inventory.clearFilter')}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
